import SettingsIcon from '@mui/icons-material/Settings'
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Collapse,
    FormControlLabel,
    Paper,
    Slider,
    Stack,
    Switch,
    Tooltip,
    Typography,
} from '@mui/material'
import debounce from 'lodash-es/debounce'
import React, { useEffect, useRef, useState } from 'react'
import { isCheckmate, isStalemate } from '../../../../chess/core'
import { positionFromFEN } from '../../../../chess/fen'
import { Move } from '../../../../chess/types'
import { Square } from '../../../../react-chessboard/src/chessboard/types'
import { saveUserSettings } from '../../../../sharedComponents/src/globalHeader/functions/ams-functions'
import { Settings } from '../../../../sharedComponents/src/globalHeader/GlobalHeader'
import { useStoreActions, useStoreState } from '../../../../store/hooks'
import { StockfishLine } from '../../useStockfish/StockfishLine'
import { StockEngineResultType } from '../../useStockfish/useStockFish'

type Props = {
    isEngineLoaded: boolean
    startEngine: () => void
    stopEngine: () => void
    allVariations: StockEngineResultType
    addEngineMove?: (move: Move | Move[]) => void
    colorToMove: number
    isCalculating: boolean
}

const EngineSection: React.FC<Props> = ({
    isEngineLoaded,
    startEngine,
    stopEngine,
    allVariations,
    addEngineMove,
    colorToMove,
    isCalculating,
}) => {
    const userData = useStoreState((state) => state.userData)
    const settings = useStoreState((state) => state.gameView.settings)

    const setSettings = useStoreActions((state) => state.gameView.setSettings)
    const setUserData = useStoreActions((state) => state.setUserData)
    const setCustomArrow = useStoreActions((state) => state.analysisMode.setCustomArrow)
    const clearCustomArrows = useStoreActions((state) => state.analysisMode.clearCustomArrows)
    const clearArrowData = useStoreActions((state) => state.analysisMode.clearArrowData)

    const [finishText, setFinishText] = useState<string>('')
    const [settingsVisibility, setSettingsVisibility] = useState<boolean>(false)
    const [hoveredLine, setHoveredLine] = useState<string | undefined>(undefined)
    const [lockedLine, setLockedLine] = useState<string | undefined>(undefined)

    const containerRef = useRef<HTMLDivElement>(null)
    const canUseVariations = settings.highlightTopMoves && settings.multipleLines > 1 && !isCalculating

    // Check for checkmate or stalemate
    useEffect(() => {
        if (isCalculating === false) {
            const checkMated = Object.values(allVariations).every((variation) => !Boolean(variation.scoreType))
            if (checkMated) {
                const position = positionFromFEN(allVariations.v1.startFen)
                const isMate = isCheckmate(position)
                if (isMate) {
                    setFinishText(`${colorToMove === 1 ? 'White' : 'Black'} Checkmated`)
                } else {
                    const isStale = isStalemate(position)
                    if (isStale) {
                        setFinishText(`${colorToMove === 1 ? 'White' : 'Black'} Stalemated`)
                    }
                }
            } else {
                setFinishText('')
            }
        } else {
            setFinishText('')
        }
    }, [allVariations, isCalculating])

    // Set the arrow for the top move
    const setArrowHandler = (key: string) => {
        const variation = allVariations[key]
        const nextMove = variation.moves[0]
        if (nextMove) {
            const from = nextMove.slice(0, 2) as Square
            const to = nextMove.slice(2, 4) as Square
            const score = parseInt(variation.score)
            setCustomArrow({
                from: from,
                to: to,
                score: score,
                scoreType: variation.scoreType,
                key: key,
            })
        }
    }

    // Prepare the arrows for the top moves
    useEffect(() => {
        if (settings.highlightTopMoves && finishText === '') {
            if (lockedLine || hoveredLine) {
                clearArrowData()
                const key = lockedLine || hoveredLine
                key && setArrowHandler(key)
            } else {
                Object.keys(allVariations).map((key) => {
                    setArrowHandler(key)
                })
            }
        } else {
            clearCustomArrows()
        }
    }, [allVariations, settings.highlightTopMoves, finishText, lockedLine, hoveredLine])

    // Clear the locked line and hovered line
    useEffect(() => {
        if (lockedLine) {
            setLockedLine(undefined)
            document.removeEventListener('mousedown', handleClickOutside)
        }
        hoveredLine && setHoveredLine(undefined)
    }, [settings.highlightTopMoves, settings.engineToggle, settings.multipleLines])

    // Start or stop the engine
    useEffect(() => {
        if (!isEngineLoaded && (settings.engineToggle || settings.evaluationBar)) {
            startEngine()
        } else if (isEngineLoaded && !settings.engineToggle && !settings.evaluationBar) {
            stopEngine()
        }
    }, [settings.engineToggle, settings.evaluationBar])

    // Clear the arrows when multiple lines change
    useEffect(() => {
        clearArrowData()
    }, [settings.multipleLines])

    const onEngineChange = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        event.stopPropagation()
        event.preventDefault()
        saveEngineSettings({ engineToggle: checked, highlightTopMoves: checked ? settings.highlightTopMoves : false })
    }

    const onEvalBarChange = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        saveEngineSettings({ evaluationBar: checked })
    }

    const onHighlightsTopMovesChange = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        saveEngineSettings({ engineToggle: true, highlightTopMoves: checked })
    }

    const onMultipleLinesChange = (event: Event, value: number | number[]) => {
        if (typeof value === 'number') {
            saveEngineSettings({ engineToggle: true, multipleLines: value })
        }
    }

    const saveEngineSettings = async (partialSettings: Partial<Settings>) => {
        if (userData.userData) {
            const updatedSettings = { ...settings, ...partialSettings }
            setUserData({ ...userData, userData: { ...userData.userData, game_settings: updatedSettings } })
            setSettings(updatedSettings)
            await saveUserSettings(updatedSettings)
        }
    }

    const debouncedSetHoveredLine = debounce((newLine: string | undefined) => {
        clearArrowData()
        setHoveredLine(newLine)
    }, 100)

    // Set the opacity for the lines
    const getOpacity = (key: string) => {
        if (isCalculating) {
            return 1
        } else if (lockedLine) {
            return key === lockedLine ? 1 : 0.5
        } else if (hoveredLine) {
            return key === hoveredLine ? 1 : 0.5
        }
        return 1
    }

    // Handle the click outside of the engine lines
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
        if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
            setLockedLine(undefined)
            setHoveredLine(undefined)
            clearArrowData()
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }

    const onLineClick = (key: string) => {
        if (canUseVariations) {
            setLockedLine(key)
            setHoveredLine(undefined)
            document.addEventListener('mousedown', handleClickOutside)
        }
    }

    const onLineHover = (key: string | undefined) => {
        if (!lockedLine && canUseVariations) {
            debouncedSetHoveredLine(key)
        }
    }

    return (
        <>
            <Accordion
                sx={{
                    py: 0,
                    px: 1.5,
                    m: 0,
                }}
                expanded={settingsVisibility}
                onChange={(event, expanded) => setSettingsVisibility(expanded)}
            >
                <AccordionSummary
                    sx={{
                        p: 0,
                        pl: 1,
                        minHeight: 'auto',
                        '& .MuiAccordionSummary-content': {
                            m: 0,
                            '&.Mui-expanded': {
                                m: 0,
                            },
                        },
                        '&.Mui-expanded': {
                            minHeight: 'auto',
                        },
                        '& .MuiAccordionSummary-expandIconWrapper': {
                            backgroundColor: settingsVisibility ? 'text.primary' : 'background.paper',
                            p: 0.5,
                            transition: 'background-color 150ms',
                        },
                    }}
                    expandIcon={
                        <Tooltip title="Analysis settings" placement="top">
                            <SettingsIcon
                                sx={{
                                    color: settingsVisibility ? 'background.default' : 'text.primary',
                                    transition: 'color 150ms, background-color 150ms',
                                }}
                            />
                        </Tooltip>
                    }
                >
                    <Box onClick={(event) => event.stopPropagation()}>
                        <FormControlLabel
                            labelPlacement="end"
                            control={
                                <Switch color="secondary" checked={settings.engineToggle} onChange={onEngineChange} />
                            }
                            label="Engine"
                        />
                    </Box>
                </AccordionSummary>

                <AccordionDetails sx={{ p: 1, py: 0, m: 0, display: 'flex', flexDirection: 'column' }}>
                    <FormControlLabel
                        labelPlacement="end"
                        control={
                            <Switch color="secondary" checked={settings.evaluationBar} onChange={onEvalBarChange} />
                        }
                        label="Evaluation Bar"
                    />
                    <FormControlLabel
                        labelPlacement="end"
                        control={
                            <Switch
                                color="secondary"
                                checked={settings.highlightTopMoves}
                                onChange={onHighlightsTopMovesChange}
                            />
                        }
                        label="Highlight Top Moves"
                    />
                    <Stack direction="row" gap={2} alignItems="center" sx={{ pt: 1 }}>
                        <Typography variant="caption" sx={{ textTransform: 'capitalize', textWrap: 'noWrap' }}>
                            Multiple Lines:
                        </Typography>
                        <Slider
                            value={settings.multipleLines}
                            step={1}
                            min={1}
                            max={3}
                            marks
                            onChange={onMultipleLinesChange}
                            sx={{ color: 'text.primary' }}
                        />
                        <Typography variant="caption">{`${settings.multipleLines}/3`}</Typography>
                    </Stack>
                </AccordionDetails>
            </Accordion>
            {isEngineLoaded && settings.engineToggle && (
                <Stack
                    ref={containerRef}
                    direction="row"
                    alignItems={'center'}
                    width="100%"
                    sx={{
                        '*': {
                            borderRadius: '6px',
                        },
                    }}
                >
                    <Box sx={{ flexGrow: 1, minWidth: '0' }}>
                        <Collapse in={isEngineLoaded}>
                            <Paper className={'StockFishEngine'}>
                                <Stack direction="column" gap={1}>
                                    {finishText !== '' ? (
                                        <Typography ml={1} color="text.disabled" variant="body2">
                                            {finishText}
                                        </Typography>
                                    ) : (
                                        Object.keys(allVariations).map((key, index) => {
                                            const variation = allVariations[key]
                                            return (
                                                variation.startFen && (
                                                    <Stack
                                                        key={index}
                                                        onMouseEnter={(e) => onLineHover(key)}
                                                        onMouseLeave={(e) => onLineHover(undefined)}
                                                        onClick={(e) => onLineClick(key)}
                                                        sx={{
                                                            opacity: getOpacity(key),
                                                            transition: 'opacity 150ms',
                                                            cursor: canUseVariations ? 'pointer' : 'default',
                                                        }}
                                                    >
                                                        <StockfishLine
                                                            isCalculating={isCalculating}
                                                            analysisResults={variation}
                                                            onMoveSelected={
                                                                addEngineMove
                                                                    ? addEngineMove
                                                                    : () => console.log('no addEngineMove function')
                                                            }
                                                            colorToMove={colorToMove}
                                                        />
                                                    </Stack>
                                                )
                                            )
                                        })
                                    )}
                                </Stack>
                            </Paper>
                        </Collapse>
                    </Box>
                </Stack>
            )}
        </>
    )
}

export default EngineSection
